import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as couponApi from '../services/coupon.service';
import type { CouponDetail, CouponListFilters, CreateCouponInput, UpdateCouponInput } from '../types';

export const couponKeys = {
  all: ['admin', 'coupons'] as const,
  list: (f: CouponListFilters) => [...couponKeys.all, 'list', f] as const,
  detail: (id: string) => [...couponKeys.all, 'detail', id] as const,
  usage: (id: string) => [...couponKeys.all, 'usage', id] as const,
};

/** Cursor-paginated (backend orders createdAt desc) — "Load more", not page numbers. */
export const useCouponsList = (filters: CouponListFilters) =>
  useInfiniteQuery({
    queryKey: couponKeys.list(filters),
    queryFn: ({ pageParam }) => couponApi.listCoupons(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
  });

export const useCouponDetail = (id: string | undefined) =>
  useQuery({
    queryKey: couponKeys.detail(id ?? ''),
    queryFn: () => couponApi.getCoupon(id as string),
    enabled: !!id,
  });

export const useCouponUsage = (id: string) =>
  useInfiniteQuery({
    queryKey: couponKeys.usage(id),
    queryFn: ({ pageParam }) => couponApi.listCouponUsage(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
  });

export const useCollectionOptions = () =>
  useQuery({
    queryKey: ['admin', 'collections', 'coupon-scope-picker'],
    queryFn: couponApi.listCollectionOptions,
    staleTime: 60_000,
  });

/** Every coupon mutation returns the fresh coupon; seed the detail cache and refresh lists. */
function useCouponMutation<TVars>(fn: (vars: TVars) => Promise<CouponDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (coupon) => {
      qc.setQueryData(couponKeys.detail(coupon.id), coupon);
      void qc.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export const useCreateCoupon = () => useCouponMutation((input: CreateCouponInput) => couponApi.createCoupon(input));

export const useUpdateCoupon = (id: string) =>
  useCouponMutation((input: UpdateCouponInput) => couponApi.updateCoupon(id, input));

export const useActivateCoupon = (id: string) => useCouponMutation<void>(() => couponApi.activateCoupon(id));

export const useDeactivateCoupon = (id: string) =>
  useCouponMutation((reason: string | undefined) => couponApi.deactivateCoupon(id, reason));
